/**
 * 로그인 — **우리 아이디·비밀번호 + JWT** (2026-10-20, DB #88·#89).
 *
 * 전에는 Firebase Authentication(이메일/비밀번호 + 구글 로그인)이었다. 그런데 관리자
 * 백엔드가 **앱과 같은 Firebase 프로젝트**를 보고 있어서 앱에서 로그인한 사람의 토큰도
 * 관리자에서 유효했다 — 관리자 본인이 앱도 쓰면(같은 이메일은 Firebase 기본 설정상
 * 한 계정이다) 앱 토큰으로 관리자가 열렸다. 이제 관리자는 우리 로그인만 쓰고,
 * 관리자 백엔드는 Firebase 를 아예 모른다.
 *
 * ⚠️ 토큰은 **우리 서버가 서명한 JWT** 다. 세 가지를 알아 두면 된다.
 *
 *   ① **일하는 동안에는 끊기지 않는다.** 남은 수명이 절반 아래로 내려가면 서버가 응답에
 *      새 토큰을 실어 보내고(`X-Admin-Token`) 요청 가로채기가 갈아 끼운다. 화면을 보고만
 *      있어도 `startHeartbeat()` 가 주기적으로 신호를 보내 수명을 늘린다.
 *   ② **비밀번호를 바꾸면 다른 기기가 그 자리에서 끊긴다.** 토큰에 '비밀번호 세대' 가
 *      담겨 있고 서버가 요청마다 DB 와 견준다.
 *   ③ **끊기면 알린다.** 가로채기가 401 을 받으면 `onSessionLost` 를 부르고,
 *      `App.vue` 가 팝업을 띄운 뒤 로그인 화면으로 보낸다.
 *
 * 토큰은 `localStorage` 에 둔다 — 새로 고쳐도 로그인이 남아야 한다.
 */
import { readonly, ref } from 'vue'

/** 토큰을 담는 자리 — 바꾸면 모두가 한 번 로그아웃된다 */
const TOKEN_KEY = 'admin_token'

/** 지금 로그인한 관리자 */
export interface AdminMe {
  admin_id: number
  login_id: string
  name: string | null
  /** admin 전체 · manager */
  role: string
  /** 관리자 명단을 다룰 수 있는가 */
  is_admin: boolean
  /** 비밀번호를 바꿔야 하는 상태인가 — 화면이 바꾸기 창을 띄운다 */
  must_change_pw: boolean
}

const token = ref<string | null>(readToken())
const me = ref<AdminMe | null>(null)

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    // 사생활 보호 창에서는 막힐 수 있다 — 그때는 이 탭에서만 유지된다
    return null
  }
}

function writeToken(v: string | null) {
  token.value = v
  try {
    if (v) localStorage.setItem(TOKEN_KEY, v)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* 못 적어도 이 탭에서는 ref 로 돈다 */
  }
}

/** 지금 로그인한 관리자. 없으면 null */
export const user = readonly(me)

/** 토큰을 들고 있는가 — 라우터 가드가 본다. 살아 있는지는 서버만 안다 */
export function isSignedIn() {
  return token.value !== null
}

/** 백엔드에 보낼 토큰 */
export async function getIdToken(): Promise<string | null> {
  return token.value
}

/**
 * 서버가 보내 준 새 토큰으로 갈아 끼운다 — 요청 가로채기가 부른다.
 *
 * 수명이 절반 아래로 내려갈 때마다 한 번씩 일어난다. 탭이 여럿이면 각자 갈아 끼우는데,
 * 어느 것이든 유효하므로 서로 덮어써도 문제가 없다.
 */
export function slideToken(next: string) {
  if (next && next !== token.value) writeToken(next)
}

/**
 * 로그인 상태를 되살린다 — 토큰이 있으면 서버에 내가 누구인지 물어본다.
 *
 * **라우터 가드가 기다린다.** 토큰이 살아 있는지는 서버만 알기 때문에, 물어보기 전에
 * 화면을 그리면 끊긴 토큰으로 들어가 첫 요청에서 401 이 난다.
 */
let readyPromise: Promise<void> | null = null
export function whenAuthReady(): Promise<void> {
  if (!readyPromise) readyPromise = restore()
  return readyPromise
}

async function restore() {
  if (!token.value) return
  try {
    const { fetchMe } = await import('@/api/adminAuth')
    me.value = await fetchMe()
  } catch {
    // 끊긴 토큰이다 — 버린다. 가드가 로그인 화면으로 보낸다.
    // 처음 들어올 때는 팝업을 띄우지 않는다(아직 아무것도 안 하고 있었다)
    writeToken(null)
    me.value = null
  }
}

/** 아이디·비밀번호 로그인 */
export async function signIn(loginId: string, password: string) {
  const { login } = await import('@/api/adminAuth')
  const r = await login(loginId, password)
  writeToken(r.token)
  me.value = r.me
  return r.me
}

/**
 * 로그아웃.
 *
 * 서버에도 알리지만 **지우는 것은 이 기기의 토큰**이다 — JWT 라 서버에 세션이 없다.
 * 모든 기기를 즉시 끊으려면 비밀번호를 바꾼다.
 */
export async function logout() {
  try {
    const { logout: serverLogout } = await import('@/api/adminAuth')
    await serverLogout()
  } catch {
    /* 서버가 안 되더라도 이 기기에서는 나간다 */
  }
  writeToken(null)
  me.value = null
}

/** 로그인한 뒤 `me` 를 다시 받아 둔다 — 비밀번호를 바꾼 뒤 등 */
export async function refreshMe() {
  const { fetchMe } = await import('@/api/adminAuth')
  me.value = await fetchMe()
  return me.value
}

/**
 * 본인 확인 — 되돌릴 수 없는 작업 직전에 비밀번호를 다시 받는다.
 *
 * 서버가 **본인 확인 시각을 새로 찍은 토큰**을 주므로 그것으로 갈아 끼운다.
 * 세션 표가 없어(JWT) 그 시각도 토큰 안에 있다.
 *
 * @throws 비밀번호가 틀리면 요청 오류
 */
export async function reauthenticate(password: string) {
  const { reauth } = await import('@/api/adminAuth')
  const r = await reauth(password)
  if (r.token) writeToken(r.token)
}

// ── 끊김 알림 ────────────────────────────────────────────────

/**
 * 세션이 끊겼을 때 부를 함수 — `App.vue` 가 등록하고 팝업을 띄운다.
 *
 * **왜 여기서 팝업을 띄우지 않나.** 이 파일은 화면 라이브러리를 모른다(토큰만 다룬다).
 * 끊긴 사실을 알리는 것과 어떻게 보여 줄지는 다른 일이라 나눠 둔다.
 */
type SessionLostHandler = (reason: string, message: string) => void
let sessionLost: SessionLostHandler | null = null

export function onSessionLost(fn: SessionLostHandler) {
  sessionLost = fn
}

/**
 * 토큰이 끊겼다 — 가로채기가 401 을 받으면 부른다.
 *
 * 토큰을 버리고 **한 번만** 알린다. 화면 여러 곳이 동시에 401 을 받는 일이 흔한데
 * (목록 여러 개가 같이 뜬다) 그때마다 팝업을 띄우면 창이 쌓인다.
 */
let lostNotified = false
export function clearSession(reason = 'bad_token', message = '로그인이 끊겼습니다') {
  const had = token.value !== null
  writeToken(null)
  me.value = null
  if (had && !lostNotified) {
    lostNotified = true
    sessionLost?.(reason, message)
    // 다음 로그인에서는 다시 알릴 수 있게 풀어 둔다
    setTimeout(() => (lostNotified = false), 3000)
  }
}

// ── 작업 중이면 끊지 않기 ────────────────────────────────────

/**
 * 심장박동 — **보고만 있어도 로그인을 유지한다** (2026-10-20 사용자 요청).
 *
 * 토큰 수명은 요청을 보낼 때마다 늘어나는데, 글을 길게 쓰거나 지도를 보고만 있으면
 * 요청이 한 번도 안 나간다. 그러면 **일하는 중인데 끊긴다.** 그래서 주기적으로 가벼운
 * 요청을 보내 수명을 늘린다.
 *
 * 두 가지를 지킨다.
 *   · **탭이 보일 때만** 보낸다 — 열어 둔 채 잊은 탭이 영원히 로그인을 붙잡지 않게.
 *   · **사람이 움직였을 때만** 보낸다 — 마우스·키·스크롤·터치가 한 번이라도 있어야 한다.
 *     아무도 안 만지는 화면은 정해진 시간이 지나면 끊긴다.
 */
const HEARTBEAT_MS = 5 * 60 * 1000
let beatTimer: ReturnType<typeof setInterval> | undefined
let activeSince = false

function markActive() {
  activeSince = true
}

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const

export function startHeartbeat() {
  if (beatTimer) return
  for (const e of ACTIVITY_EVENTS) {
    window.addEventListener(e, markActive, { passive: true })
  }
  beatTimer = setInterval(async () => {
    if (!token.value) return
    if (document.visibilityState !== 'visible') return
    if (!activeSince) return
    activeSince = false
    try {
      const { ping } = await import('@/api/adminAuth')
      await ping()
    } catch {
      // 끊겼으면 가로채기가 이미 알렸다
    }
  }, HEARTBEAT_MS)
}

export function stopHeartbeat() {
  if (beatTimer) clearInterval(beatTimer)
  beatTimer = undefined
  for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, markActive)
}
